/**
 * Hook for performing tag mutations on kanban cards
 * @remarks
 * This hook provides a clean Apollo-based interface for adding, removing, and setting tags on player cards.
 * It uses optimistic responses for instant UI feedback (0ms perceived latency) and relies on Apollo's
 * automatic cache normalization for updates when server responses include __typename and id fields.
 * Replaces the custom localStorage + event emitter system with Apollo's built-in cache management.
 * @example
 * ```tsx
 * const {
 *   addTagsToPlayer,
 *   removeTagsFromPlayer,
 *   setPlayerTags,
 *   isAddingTags,
 *   isRemovingTags,
 *   isSettingTags,
 *   error
 * } = useTagMutations(boardId);
 *
 * // Add tags to a player
 * await addTagsToPlayer(playerId, ['tag-1', 'tag-2']);
 *
 * // Remove tags from a player
 * await removeTagsFromPlayer(playerId, ['tag-1']);
 *
 * // Replace all tags for a player
 * await setPlayerTags(playerId, ['tag-3', 'tag-4']);
 * ```
 */

import type { ApolloError } from "@apollo/client";
import { useCallback, useMemo } from "react";

import {
  useAddTagsToKanbanCardMutation,
  useRemoveTagsFromKanbanCardMutation,
  useSetKanbanCardTagsMutation,
  useListKanbanCardsQuery,
} from "@/generated/graphql";
import { CARDS_PAGE_SIZE } from "@/features/player-kanban/constants";

/**
 * Return type for useTagMutations hook
 */
interface UseTagMutationsReturn {
  /**
   * Add tags to a player's card
   * @param playerId - The player's ID
   * @param tagIds - Array of tag IDs to add
   * @throws {Error} if card not found for player
   */
  addTagsToPlayer: (playerId: string, tagIds: string[]) => Promise<void>;

  /**
   * Remove tags from a player's card
   * @param playerId - The player's ID
   * @param tagIds - Array of tag IDs to remove
   * @throws {Error} if card not found for player
   */
  removeTagsFromPlayer: (playerId: string, tagIds: string[]) => Promise<void>;

  /**
   * Replace all tags on a player's card
   * @param playerId - The player's ID
   * @param tagIds - Array of tag IDs to set (replaces existing tags)
   * @throws {Error} if card not found for player
   */
  setPlayerTags: (playerId: string, tagIds: string[]) => Promise<void>;

  /**
   * Loading state for add tags operation
   */
  isAddingTags: boolean;

  /**
   * Loading state for remove tags operation
   */
  isRemovingTags: boolean;

  /**
   * Loading state for set tags operation
   */
  isSettingTags: boolean;

  /**
   * Error from any tag mutation operation
   */
  error: ApolloError | undefined;
}

/**
 * Hook for performing tag mutations on kanban cards using Apollo Client
 * @param boardId - The kanban board ID
 * @returns Object containing mutation functions and loading/error states
 */
export function useTagMutations(boardId: string): UseTagMutationsReturn {
  // Read the board's cards to resolve player IDs to card IDs.
  //
  // SE-5631: `cache-first`, NOT `cache-and-network`. This hook is held by
  // `usePlayerCardBase`, i.e. once per player card, so a network-touching
  // policy makes a board mount issue a request per card. `useKanbanBoardQueries`
  // owns fetching this list (also `cache-first`, same variables) and tag
  // mutations write the same normalized `KanbanCard` entities, so reading from
  // cache here stays reactive without a round-trip.
  //
  // The `first` value matches CARDS_PAGE_SIZE used by useKanbanBoardQueries so
  // both calls share the same Apollo cache entry (SE-5058).
  const { data: cardsData } = useListKanbanCardsQuery({
    variables: {
      kanbanBoardId: boardId,
      first: CARDS_PAGE_SIZE,
    },
    fetchPolicy: "cache-first",
    // The house guard (`usePlayerCardBase` sets the same one): without a board
    // there is no card list to resolve against, so the request could only ever
    // come back empty. It also lets a caller hold this hook unconditionally —
    // which the Rules of Hooks require — on a surface where the board id is
    // still optional.
    skip: !boardId,
  });

  // Setup mutation hooks
  const [addTagsMutation, { loading: isAddingTags, error: addError }] =
    useAddTagsToKanbanCardMutation();

  const [removeTagsMutation, { loading: isRemovingTags, error: removeError }] =
    useRemoveTagsFromKanbanCardMutation();

  const [setTagsMutation, { loading: isSettingTags, error: setError }] =
    useSetKanbanCardTagsMutation();

  // Combine errors from all mutations
  const error = useMemo(
    () => addError || removeError || setError,
    [addError, removeError, setError]
  );

  /**
   * Helper to resolve card ID from player ID
   */
  const resolveCardId = useCallback(
    (playerId: string): string => {
      if (!cardsData?.listKanbanCards?.edges) {
        throw new Error(`Card not found for player ${playerId}`);
      }

      const matchingEdge = cardsData.listKanbanCards.edges.find(
        edge => edge.node.player.id === playerId
      );

      const cardId = matchingEdge?.node.kanbanCard?.id;

      if (!cardId) {
        throw new Error(`Card not found for player ${playerId}`);
      }

      return cardId;
    },
    [cardsData]
  );

  /**
   * Helper to create base optimistic response for kanban card mutations
   * @remarks
   * These mutations select `KanbanCardWithTagsFragment`, so the response must be
   * COMPLETE against that selection set or Apollo floods error-code-13
   * "Missing field ... while writing result" (SE-5058). It must also be
   * ACCURATE: the result is a normalized `KanbanCard` entity, so every field
   * written here overwrites the cached card — a tag edit that reported a
   * placeholder ordering would visibly reshuffle the board.
   *
   * SE-5493 made that concrete. Adding `rank` (the viewer's private pipeline
   * order) to the fragment meant this builder had to supply it, and inventing a
   * value would have thrown away the very ordering the ticket exists to
   * preserve. The non-tag fields are therefore all read back from the cached
   * card and only fall back to placeholders when the card is genuinely absent.
   * @param cardId - The card ID
   * @param tagIds - Array of tag IDs for the optimistic response
   * @returns Optimistic kanban card carrying the new tag set over the cached card's unchanged fields
   */
  const createOptimisticCard = useCallback(
    (cardId: string, tagIds: string[]) => {
      const timestamp = new Date().toISOString();

      const cachedCards = cardsData?.listKanbanCards?.edges?.flatMap(
        edge => edge.node.kanbanCard ?? []
      );

      // The card this mutation targets — the source of every field a tag edit
      // must NOT change (ordering, phase, notes, creation time).
      const existingCard = cachedCards?.find(card => card.id === cardId);

      // Get existing tag data from cache to preserve colors
      const existingTagsMap = new Map(
        cachedCards?.flatMap(card => card.tags || []).map(tag => [tag.id, tag])
      );

      return {
        __typename: "KanbanCard" as const,
        id: cardId,
        position: existingCard?.position ?? 0,
        rank: existingCard?.rank ?? 1,
        notes: existingCard?.notes ?? null,
        kanbanPhaseId: existingCard?.kanbanPhaseId ?? "",
        // SE-5497: enteredStageAt joined KanbanCardFragment, so it is part of
        // this mutation's selection set. Preserve the cached value (a tag edit
        // never changes the stage) — omitting it would flood error-code-13
        // "Missing field `enteredStageAt`" (SE-5058). Falls back to the card's
        // createdAt, then now, for a fixture/partial cache entry.
        enteredStageAt:
          existingCard?.enteredStageAt ?? existingCard?.createdAt ?? timestamp,
        createdAt: existingCard?.createdAt ?? timestamp,
        /*
         * Bumped for ALL THREE tag operations — this builder is shared by add,
         * remove and set, so the card's "Updated Nd ago" footer moves the
         * moment any of them fires.
         *
         * It does NOT stick, and that is a SERVER gap rather than a hole here.
         * None of `addTagsToKanbanCard`, `removeTagsFromKanbanCard` or
         * `setKanbanCardTags` writes the `kanban_card` row at all: each reads
         * the card, mutates only the `KanbanCardTag` junction table, and
         * returns the card entity exactly as it was read
         * (`backend-v2` `src/kanban/kanban.service.ts`). So the response Apollo
         * replaces this optimistic entry with carries the OLD `updatedAt`, and
         * the footer snaps back.
         *
         * Making the timestamps differ per operation here would only change how
         * long the wrong value is displayed. If tag edits are meant to count as
         * card activity, the fix is a backend one (touch the card row in those
         * three transactions).
         */
        updatedAt: timestamp,
        tags: tagIds.map(tagId => {
          const existingTag = existingTagsMap.get(tagId);
          return {
            __typename: "KanbanTag" as const,
            id: tagId,
            name: existingTag?.name || "",
            cardCount: existingTag?.cardCount || 0,
            color: existingTag?.color || null, // Preserve color from cache
            createdAt: existingTag?.createdAt || timestamp,
            updatedAt: existingTag?.updatedAt || timestamp,
          };
        }),
        kanbanPhase: {
          __typename: "KanbanPhase" as const,
          // Optional-chained through `kanbanPhase` even though the schema types
          // it non-null: a partially-written cache entry (and every test
          // fixture that omits it) would otherwise crash the whole tag
          // mutation on a field this builder only reads for continuity.
          id: existingCard?.kanbanPhase?.id ?? "",
          name: existingCard?.kanbanPhase?.name ?? "",
        },
      };
    },
    [cardsData]
  );

  /**
   * Add tags to a player's card
   */
  const addTagsToPlayer = useCallback(
    async (playerId: string, tagIds: string[]) => {
      const cardId = resolveCardId(playerId);

      // Get existing tags from cache to preserve them in optimistic response
      const existingCard = cardsData?.listKanbanCards?.edges?.find(
        edge => edge.node.player.id === playerId
      )?.node.kanbanCard;
      const existingTagIds = existingCard?.tags?.map(t => t.id) ?? [];
      const mergedTagIds = [...existingTagIds, ...tagIds];

      await addTagsMutation({
        variables: { cardId, tagIds, kanbanBoardId: boardId },
        optimisticResponse: {
          addTagsToKanbanCard: createOptimisticCard(cardId, mergedTagIds),
        },
      });
    },
    [addTagsMutation, resolveCardId, createOptimisticCard, cardsData, boardId]
  );

  /**
   * Remove tags from a player's card
   */
  const removeTagsFromPlayer = useCallback(
    async (playerId: string, tagIds: string[]) => {
      const cardId = resolveCardId(playerId);

      // Get existing tags from cache and filter out tags being removed
      const existingCard = cardsData?.listKanbanCards?.edges?.find(
        edge => edge.node.player.id === playerId
      )?.node.kanbanCard;
      const existingTagIds = existingCard?.tags?.map(t => t.id) ?? [];
      const remainingTagIds = existingTagIds.filter(id => !tagIds.includes(id));

      await removeTagsMutation({
        variables: { cardId, tagIds, kanbanBoardId: boardId },
        optimisticResponse: {
          removeTagsFromKanbanCard: createOptimisticCard(
            cardId,
            remainingTagIds
          ),
        },
      });
    },
    [
      removeTagsMutation,
      resolveCardId,
      createOptimisticCard,
      cardsData,
      boardId,
    ]
  );

  /**
   * Replace all tags on a player's card
   */
  const setPlayerTags = useCallback(
    async (playerId: string, tagIds: string[]) => {
      const cardId = resolveCardId(playerId);

      await setTagsMutation({
        variables: { cardId, tagIds, kanbanBoardId: boardId },
        optimisticResponse: {
          setKanbanCardTags: createOptimisticCard(cardId, tagIds),
        },
      });
    },
    [setTagsMutation, resolveCardId, createOptimisticCard, boardId]
  );

  return {
    addTagsToPlayer,
    removeTagsFromPlayer,
    setPlayerTags,
    isAddingTags,
    isRemovingTags,
    isSettingTags,
    error,
  };
}
